/* platform/ActivityTimeline — Operational business-event feed.
   Reads from the audit API filtered to business categories only.
   NOT a second audit log — presents narrative events, not technical details. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listTimeline } from '../api/audit.api.js';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill, Btn } from '../shared/ui';
import { PageHead } from './_ui';
import { exportCSV } from '../shared/data-table';

const PAGE_SIZE = 100;

/* ── action → display metadata ─────────────────────────────── */
const ACTION_META = {
  // Org lifecycle
  'created org':           { icon: 'Building2',    tone: 'blue',   label: 'Org Created' },
  'provisioned org':       { icon: 'Building2',    tone: 'green',  label: 'Org Provisioned' },
  'activated org':         { icon: 'CheckCircle2', tone: 'green',  label: 'Org Activated' },
  'org activated':         { icon: 'CheckCircle2', tone: 'green',  label: 'Org Activated' },
  'suspended org':         { icon: 'PauseCircle',  tone: 'amber',  label: 'Org Suspended' },
  'archived org':          { icon: 'Archive',      tone: 'gray',   label: 'Org Archived' },
  'restored org':          { icon: 'RefreshCw',    tone: 'blue',   label: 'Org Restored' },
  'deleted org':           { icon: 'Trash2',       tone: 'red',    label: 'Org Deleted' },
  'updated org':           { icon: 'Edit3',        tone: 'gray',   label: 'Org Updated' },
  'invite generated':      { icon: 'Link',         tone: 'blue',   label: 'Invite Link Generated' },
  'invite accepted':       { icon: 'UserCheck',    tone: 'green',  label: 'Invite Accepted' },
  // Workspace lifecycle
  'created workspace':     { icon: 'LayoutGrid',   tone: 'blue',   label: 'Workspace Created' },
  'archived workspace':    { icon: 'Archive',      tone: 'gray',   label: 'Workspace Archived' },
  'workspace.activated':   { icon: 'CheckCircle2', tone: 'green',  label: 'Workspace Activated' },
  'workspace.archived':    { icon: 'Archive',      tone: 'gray',   label: 'Workspace Archived' },
  'workspace.suspended':   { icon: 'PauseCircle',  tone: 'amber',  label: 'Workspace Suspended' },
  'workspace.deleted':     { icon: 'Trash2',       tone: 'red',    label: 'Workspace Deleted' },
  'workspace.updated':     { icon: 'Edit3',        tone: 'gray',   label: 'Workspace Updated' },
  // Plan lifecycle
  'created plan':          { icon: 'Package',      tone: 'blue',   label: 'Plan Created' },
  'updated plan':          { icon: 'Edit3',        tone: 'gray',   label: 'Plan Updated' },
  'archived plan':         { icon: 'Archive',      tone: 'gray',   label: 'Plan Archived' },
  'restored plan':         { icon: 'RefreshCw',    tone: 'blue',   label: 'Plan Restored' },
  'activated plan':        { icon: 'CheckCircle2', tone: 'green',  label: 'Plan Activated' },
  // Subscription
  'provisioned subscription':  { icon: 'CreditCard',  tone: 'blue',   label: 'Subscription Created' },
  'activated subscription':    { icon: 'CheckCircle2',tone: 'green',  label: 'Subscription Activated' },
  'updated subscription':      { icon: 'Edit3',       tone: 'gray',   label: 'Subscription Updated' },
  'renewed subscription':      { icon: 'RefreshCw',   tone: 'green',  label: 'Subscription Renewed' },
  'expired subscription':      { icon: 'Clock',       tone: 'amber',  label: 'Subscription Expired' },
  'suspended subscription':    { icon: 'PauseCircle', tone: 'amber',  label: 'Subscription Suspended' },
  'cancelled subscription':    { icon: 'XCircle',     tone: 'red',    label: 'Subscription Cancelled' },
  'reactivated subscription':  { icon: 'RotateCcw',   tone: 'green',  label: 'Subscription Reactivated' },
  'auto-provisioned subscription': { icon: 'Zap',     tone: 'blue',   label: 'Subscription Auto-Provisioned' },
  // License
  'assigned license':               { icon: 'Key',       tone: 'purple', label: 'License Assigned' },
  'added seats':                    { icon: 'UserPlus',  tone: 'green',  label: 'Seats Added' },
  'updated license':                { icon: 'Edit3',     tone: 'gray',   label: 'License Updated' },
  'renewed license':                { icon: 'RefreshCw', tone: 'green',  label: 'License Renewed' },
  'deleted license':                { icon: 'Trash2',    tone: 'red',    label: 'License Deleted' },
  'auto-provisioned missing licenses': { icon: 'Zap',   tone: 'blue',   label: 'Licenses Auto-Provisioned' },
  // Partner
  'added partner':         { icon: 'Briefcase',    tone: 'blue',   label: 'Partner Added' },
  'activated partner':     { icon: 'CheckCircle2', tone: 'green',  label: 'Partner Activated' },
  'deactivated partner':   { icon: 'Briefcase',    tone: 'gray',   label: 'Partner Deactivated' },
  // Platform Users
  'platform.user.invited':          { icon: 'Mail',      tone: 'blue',   label: 'User Invited' },
  'platform.invite.accepted':       { icon: 'UserCheck', tone: 'green',  label: 'Invite Accepted' },
  'provisioned org admin':          { icon: 'UserCog',   tone: 'green',  label: 'Org Admin Provisioned' },
  'platform_user.force_logout':     { icon: 'LogOut',    tone: 'amber',  label: 'User Force Logged Out' },
  'platform_user.suspended':        { icon: 'UserX',     tone: 'red',    label: 'User Suspended' },
  'platform_user.activated':        { icon: 'UserCheck', tone: 'green',  label: 'User Activated' },
  'platform_user.access_revoked':   { icon: 'UserMinus', tone: 'red',    label: 'User Access Revoked' },
  // Roles
  'platform.role.created':          { icon: 'Shield',    tone: 'blue',   label: 'Role Created' },
  'platform.role.updated':          { icon: 'Shield',    tone: 'gray',   label: 'Role Updated' },
  'platform.role.deleted':          { icon: 'Trash2',    tone: 'red',    label: 'Role Deleted' },
  'platform.role.duplicated':       { icon: 'Copy',      tone: 'blue',   label: 'Role Duplicated' },
  'platform_user.custom_role_assigned': { icon: 'Shield', tone: 'blue',  label: 'Custom Role Assigned' },
  // Settings
  'settings.updated.general':       { icon: 'Settings',  tone: 'gray',   label: 'Settings Updated' },
  'updated branding':               { icon: 'Palette',   tone: 'blue',   label: 'Branding Updated' },
  'settings.branding_asset_uploaded': { icon: 'Palette', tone: 'blue',   label: 'Branding Asset Uploaded' },
  // Security
  'login failed':                   { icon: 'AlertTriangle', tone: 'red', label: 'Login Failed' },
  'login blocked — account locked': { icon: 'Lock',      tone: 'red',    label: 'Account Locked' },
  // Backup
  'triggered manual platform backup':  { icon: 'HardDrive', tone: 'blue',  label: 'Backup Started' },
  'initiated platform restore from backup': { icon: 'HardDrive', tone: 'amber', label: 'Restore Initiated' },
  // Member (workspace-level)
  'invited member':   { icon: 'Mail',      tone: 'blue',  label: 'Member Invited' },
  'activated member': { icon: 'UserCheck', tone: 'green', label: 'Member Activated' },
  'suspended member': { icon: 'UserX',     tone: 'amber', label: 'Member Suspended' },
  'removed member':   { icon: 'UserMinus', tone: 'gray',  label: 'Member Removed' },
};

const PREFIX_META = [
  { prefix: 'platform_user.role_changed',        meta: { icon: 'Shield',      tone: 'amber', label: 'User Role Changed' } },
  { prefix: 'platform backup completed',         meta: { icon: 'CheckCircle2',tone: 'green', label: 'Backup Completed' } },
  { prefix: 'platform backup failed',            meta: { icon: 'AlertCircle', tone: 'red',   label: 'Backup Failed' } },
  { prefix: 'platform restore completed',        meta: { icon: 'CheckCircle2',tone: 'green', label: 'Restore Completed' } },
  { prefix: 'platform restore failed',           meta: { icon: 'AlertCircle', tone: 'red',   label: 'Restore Failed' } },
  { prefix: 'renewed license for',               meta: { icon: 'RefreshCw',   tone: 'green', label: 'License Renewed' } },
  { prefix: 'initiated platform restore',        meta: { icon: 'HardDrive',   tone: 'amber', label: 'Restore Initiated' } },
];

const CAT_FALLBACK = {
  Org:          { icon: 'Building2',    tone: 'blue',   label: 'Org Event' },
  Workspace:    { icon: 'LayoutGrid',   tone: 'blue',   label: 'Workspace Event' },
  Plan:         { icon: 'Package',      tone: 'blue',   label: 'Plan Event' },
  Subscription: { icon: 'CreditCard',   tone: 'blue',   label: 'Subscription Event' },
  License:      { icon: 'Key',          tone: 'purple', label: 'License Event' },
  Partner:      { icon: 'Briefcase',    tone: 'blue',   label: 'Partner Event' },
  PlatformUser: { icon: 'User',         tone: 'blue',   label: 'User Event' },
  Role:         { icon: 'Shield',       tone: 'blue',   label: 'Role Event' },
  Settings:     { icon: 'Settings',     tone: 'gray',   label: 'Settings Event' },
  Backup:       { icon: 'HardDrive',    tone: 'blue',   label: 'Backup Event' },
  Security:     { icon: 'AlertTriangle',tone: 'red',    label: 'Security Event' },
  Member:       { icon: 'Users',        tone: 'blue',   label: 'Member Event' },
};

function getEventMeta(action, cat) {
  if (!action) return CAT_FALLBACK[cat] ?? { icon: 'Activity', tone: 'gray', label: 'Event' };
  const direct = ACTION_META[action];
  if (direct) return direct;
  const found = PREFIX_META.find(p => action.startsWith(p.prefix));
  if (found) return found.meta;
  return CAT_FALLBACK[cat] ?? { icon: 'Activity', tone: 'gray', label: action.replaceAll('_', ' ') };
}

/* ── date helpers ───────────────────────────────────────────── */
const SKELETON_WIDTHS = [
  { key: 'dot',    w: 22  },
  { key: 'label',  w: 120 },
  { key: 'actor',  w: 100 },
  { key: 'target', w: 160 },
  { key: 'time',   w: 60  },
];

function extractDateStr(createdAt) {
  if (!createdAt) return 'Unknown';
  return String(createdAt).split('T')[0];
}

function dateLabel(dateStr) {
  if (!dateStr || dateStr === 'Unknown') return 'Unknown';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yday  = new Date(today); yday.setDate(yday.getDate() - 1);
  const d     = new Date(dateStr + 'T00:00:00');
  d.setHours(0, 0, 0, 0);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === yday.getTime())  return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtTime(createdAt) {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

/* ── filter config ──────────────────────────────────────────── */
const FILTERS = [
  { label: 'All',           cat: null },
  { label: 'Orgs',          cat: 'Org' },
  { label: 'Workspaces',    cat: 'Workspace' },
  { label: 'Plans',         cat: 'Plan' },
  { label: 'Subscriptions', cat: 'Subscription' },
  { label: 'Licenses',      cat: 'License' },
  { label: 'Partners',      cat: 'Partner' },
  { label: 'Users',         cat: 'PlatformUser' },
  { label: 'Members',       cat: 'Member' },
  { label: 'Roles',         cat: 'Role' },
  { label: 'Security',      cat: 'Security' },
];

/* ── component ──────────────────────────────────────────────── */
export default function ActivityTimeline() {
  const navigate   = useNavigate();
  const [activeCat, setActiveCat] = useState(null);
  const [q, setQ]                 = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const debounceRef               = useRef(null);

  const hasMore = rows.length < total;

  const handleQ = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(val), 350);
  };

  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    setError(null);
    listTimeline({
      q:    debouncedQ || undefined,
      cat:  activeCat  || undefined,
      from: from       || undefined,
      to:   to         || undefined,
      page: 1,
      limit: PAGE_SIZE,
    })
      .then(res => {
        if (!cancelled) { setRows(res.data ?? []); setTotal(res.total ?? 0); }
      })
      .catch(() => { if (!cancelled) setError('Failed to load activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ, activeCat, from, to]);

  const loadMore = useCallback(async () => {
    const nextPage = page + 1;
    setLoading(true);
    try {
      const res = await listTimeline({
        q:    debouncedQ || undefined,
        cat:  activeCat  || undefined,
        from: from       || undefined,
        to:   to         || undefined,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      setRows(prev => [...prev, ...(res.data ?? [])]);
      setPage(nextPage);
    } catch {
      setError('Failed to load more events.');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, activeCat, from, to, page]);

  const refresh = useCallback(() => { setDebouncedQ(q); }, [q]);

  const doExport = useCallback(async () => {
    try {
      const res = await listTimeline({
        q: debouncedQ || undefined, cat: activeCat || undefined,
        from: from || undefined, to: to || undefined,
        page: 1, limit: 5000,
      });
      const exportRows = (res.data ?? []).map(e => ({
        date: extractDateStr(e.createdAt),
        time: fmtTime(e.createdAt),
        category: e.cat,
        action: e.action,
        actor: e.who,
        target: e.target,
      }));
      exportCSV('activity-timeline', exportRows);
    } catch {
      /* silent — edge case, user can retry */
    }
  }, [debouncedQ, activeCat, from, to]);

  const clearFilters = () => {
    setActiveCat(null);
    setQ('');
    setFrom('');
    setTo('');
    clearTimeout(debounceRef.current);
    setDebouncedQ('');
  };

  /* Group rows by date */
  const grouped = (() => {
    const map = {};
    rows.forEach(e => {
      const d = extractDateStr(e.createdAt);
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  })();

  const hasFilters = activeCat || debouncedQ || from || to;

  function renderBody() {
    if (loading && rows.length === 0) {
      return Array.from({ length: 6 }, (_, i) => (
        <div key={`skel-${i}`} className="nx-timeline-event" style={{ opacity: 0.5 }}>
          {SKELETON_WIDTHS.map(({ key, w }) => (
            <div key={key} className="nx-cc-skeleton" style={{ height: 14, width: w, borderRadius: 4, margin: '0 6px' }} />
          ))}
        </div>
      ));
    }
    if (rows.length === 0) {
      const msg = hasFilters ? 'No events match the current filters.' : 'No business events recorded yet.';
      return (
        <div className="nx-cc-chart-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <NexusIcon name="Activity" size={32} style={{ opacity: 0.2, display: 'block', margin: '0 auto 12px' }} />
          <div style={{ color: 'var(--nx-text-muted)', fontSize: 14 }}>{msg}</div>
        </div>
      );
    }
    return grouped.map(([dateStr, items]) => (
      <div key={dateStr} className="nx-timeline-group">
        <div className="nx-timeline-date-label">
          <span>{dateLabel(dateStr)}</span>
          <span className="nx-muted nx-mini" style={{ marginLeft: 8 }}>
            {items.length}{items.length === 1 ? ' event' : ' events'}
          </span>
        </div>
        <div className="nx-timeline-events">
          {items.map(e => {
            const meta = getEventMeta(e.action, e.cat);
            return (
              <div key={e.id} className="nx-timeline-event">
                <div className={`nx-timeline-dot tone-${meta.tone}`}>
                  <NexusIcon name={meta.icon} size={12} />
                </div>
                <div className="nx-timeline-body">
                  <div className="nx-timeline-header">
                    <Pill tone={meta.tone}>{meta.label}</Pill>
                    {e.who && <span className="nx-muted nx-mini">by {e.who}</span>}
                    <span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>{fmtTime(e.createdAt)}</span>
                    {e.time && <span className="nx-muted nx-mini" style={{ marginLeft: 6, fontSize: 11 }}>({e.time})</span>}
                  </div>
                  {e.target && <div className="nx-timeline-target">{e.target}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ));
  }

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Activity Timeline"
        sub="Business events across the platform — organizations, workspaces, users, licenses"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" onClick={refresh} disabled={loading}>Refresh</Btn>
            <Btn variant="ghost" icon="Download" onClick={doExport} disabled={loading}>Export CSV</Btn>
            <Btn variant="ghost" icon="ClipboardList" onClick={() => navigate('/platform/audit')}>
              Full Audit Log
            </Btn>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {FILTERS.map(f => (
          <button
            key={f.label}
            className={`nx-filter-chip${activeCat === f.cat ? ' is-active' : ''}`}
            onClick={() => setActiveCat(f.cat)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <div className="nx-search-inline" style={{ width: 200 }}>
            <NexusIcon name="Search" size={13} />
            <input value={q} onChange={e => handleQ(e.target.value)} placeholder="Search actor or target…" />
          </div>
          <input
            type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-surface)', color: 'var(--nx-text)', height: 30 }}
            title="From date"
          />
          <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>→</span>
          <input
            type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)}
            style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-surface)', color: 'var(--nx-text)', height: 30 }}
            title="To date"
          />
          {hasFilters && (
            <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear</Btn>
          )}
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-danger-bg, #FEF2F2)', border: '1px solid var(--nx-danger-border, #FCA5A5)', borderRadius: 8, margin: '10px 0', color: 'var(--nx-danger, #DC2626)', fontSize: 13 }}>
          <NexusIcon name="AlertCircle" size={14} />
          <span>{error}</span>
          <Btn variant="ghost" size="sm" onClick={refresh} style={{ marginLeft: 'auto' }}>Retry</Btn>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {renderBody()}
      </div>

      {(hasMore || (loading && rows.length > 0)) && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <Btn variant="ghost" icon={loading ? 'Loader' : 'ChevronDown'} onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : `Load more (${rows.length} of ${total.toLocaleString()})`}
          </Btn>
        </div>
      )}
    </div>
  );
}
